'use client';

import { useState } from 'react';
import { useCities } from '@/hooks/useCities';
import CityCard from '@/components/CityCard';
import QuickCompareCities from '@/components/QuickCompareCities';
import Breadcrumb from '@/components/Breadcrumb';

export default function CitiesPage() {
  const { cities, stats, loading, error } = useCities();
  const [selectedCities, setSelectedCities] = useState<any[]>([]);

  // 处理城市选择
  const handleCitySelect = (city: any) => {
    if (selectedCities.length >= 2) {
      // 如果已经选择了2个城市，替换第一个
      setSelectedCities([selectedCities[1], city]);
    } else {
      // 添加城市到选择列表
      setSelectedCities([...selectedCities, city]);
    }
  };

  // 处理移除城市
  const handleRemoveCity = (cityId: string) => {
    setSelectedCities(selectedCities.filter(city => city.id !== cityId));
  };

  // 处理比较
  const handleCompare = () => {
    if (selectedCities.length === 2) {
      const url = `/map?a=${selectedCities[0].slug}&b=${selectedCities[1].slug}`;
      window.open(url, '_blank');
    }
  };

  // 清除选择
  const handleClear = () => {
    setSelectedCities([]);
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading cities...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-8">
      <Breadcrumb items={[
        { label: 'Browse', href: '/browse' },
        { label: 'Cities' }
      ]} />
      
      {/* 页面标题 */}
      <div className="mt-8 mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Cities</h1>
        <p className="text-lg text-slate-600">
          Compare the true size of cities worldwide and explore their geographical features.
        </p>
      </div>

      {/* 快速比较工具 */}
      <div className="mb-8">
        <QuickCompareCities 
          cities={cities}
          selectedCities={selectedCities}
          onCitySelect={handleCitySelect}
          onRemoveCity={handleRemoveCity}
          onCompare={handleCompare}
          onClear={handleClear}
        />
      </div>

      {/* 城市列表 */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {cities.map((city) => (
          <CityCard
            key={city.id}
            city={city}
            isSelected={selectedCities.some(selected => selected.id === city.id)}
            onSelect={handleCitySelect}
          />
        ))}
      </div>

      {/* 关于城市真实大小的描述 */}
      <div className="mt-12 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Understanding The True Size of Cities</h2>
        <p className="text-slate-700 leading-relaxed">
          City boundaries can be surprisingly different from what we imagine. Administrative boundaries often include vast suburban areas, 
          while some cities are more compact than expected. Our interactive platform reveals the true size of 
          cities worldwide, helping you discover surprising scale relationships. For instance, New York City&apos;s administrative area is 
          much larger than many countries, while some European capitals are surprisingly compact. Explore these fascinating urban 
          realities through our precise scale comparison tools.
        </p>
      </div>

      {/* 相关页面导航 */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">More Comparisons</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <a 
            href="/true-size-of-continents"
            className="inline-flex items-center justify-center px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 rounded-lg font-medium transition-colors border border-blue-200 hover:border-blue-300 text-sm"
          >
            True Size of Continents
          </a>
          <a 
            href="/true-size-of-countries"
            className="inline-flex items-center justify-center px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 hover:text-green-800 rounded-lg font-medium transition-colors border border-green-200 hover:border-green-300 text-sm"
          >
            True Size of Countries
          </a>
          <a 
            href="/true-size-of-oceans"
            className="inline-flex items-center justify-center px-3 py-2 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 hover:text-cyan-800 rounded-lg font-medium transition-colors border border-cyan-200 hover:border-cyan-300 text-sm"
          >
            True Size of Oceans
          </a>
          <a 
            href="/true-size-of-lakes"
            className="inline-flex items-center justify-center px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 hover:text-indigo-800 rounded-lg font-medium transition-colors border border-indigo-200 hover:border-indigo-300 text-sm"
          >
            True Size of Lakes
          </a>
          <a 
            href="/true-size-of-islands"
            className="inline-flex items-center justify-center px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 hover:text-orange-800 rounded-lg font-medium transition-colors border border-orange-200 hover:border-orange-300 text-sm"
          >
            True Size of Islands
          </a>
        </div>
      </div>

    </div>
  );
}
